import * as THREE from '../three'
import { Directions, MAX_LIGHT_LEVEL, PIXEL_SIZE } from '../../core/chunks'
import { uuid } from '../../core/utils'
import { Location } from '../../core/Location'
import { AtlasMaterial } from '../../core/AtlasMaterial'
import { DEG2RAD } from 'three/src/math/MathUtils.js'

const v1 = new THREE.Vector3()
const v2 = new THREE.Vector3()
const v3 = new THREE.Vector3()
const v4 = new THREE.Vector3()
const v5 = new THREE.Vector3()
const v6 = new THREE.Vector3()
const v7 = new THREE.Vector3()
const v8 = new THREE.Vector3()
const v9 = new THREE.Vector3()
const q1 = new THREE.Quaternion()
const q2 = new THREE.Quaternion()
const q3 = new THREE.Quaternion()
const e1 = new THREE.Euler(0, 0, 0, 'YXZ')
const loc1 = new Location()

const _arr = []

const UP = new THREE.Vector3(0, 1, 0)
const DOWN = new THREE.Vector3(0, -1, 0)
const RIGHT = new THREE.Vector3(1, 0, 0)

const HEAD_FORWARD_CORRECTION = new THREE.Quaternion().setFromAxisAngle(UP, Math.PI)

const LIGHT_TIME = 0.1

const lightOffsets = [
  new THREE.Vector3(0, 0, 0), // p
  new THREE.Vector3(1, 0, 0), // px
  new THREE.Vector3(-1, 0, 0), // nx
  new THREE.Vector3(0, 1, 0), // py
  new THREE.Vector3(0, -1, 0), // ny
  new THREE.Vector3(0, 0, 1), // pz
  new THREE.Vector3(0, 0, -1), // nz
]

// At the top of the file with other constants
const HAND_REST_OFFSET_LEFT = new THREE.Quaternion().setFromEuler(
  new THREE.Euler(45 * DEG2RAD, 180 * DEG2RAD, 0, 'XYZ')
)
const HAND_REST_OFFSET_RIGHT = new THREE.Quaternion().setFromEuler(
  new THREE.Euler(45 * DEG2RAD, 180 * DEG2RAD, 0, 'XYZ')
)

export class Avatar {
  constructor(client) {
    this.id = uuid()
    this.client = client
    this.mesh = null
    this.bonesByName = null
    this.visible = false
    this.height = 32 * PIXEL_SIZE // estimate default
    this.material = null
    this.lightArr = [MAX_LIGHT_LEVEL, MAX_LIGHT_LEVEL, MAX_LIGHT_LEVEL, MAX_LIGHT_LEVEL]
  }

  setVisible(value) {
    this.visible = value
    if (this.mesh) {
      this.mesh.visible = value
    }
  }

  async setup(avatar) {
    const result = await this.client.avatars.rpcToWorker('build', { id: this.id, avatar })
    // console.log('result', result)

    if (!this.material) {
      this.material = new AtlasMaterial({
        anisotropy: this.client.graphics.maxAnisotropy,
        skyUniforms: this.client.sky.sky.uniforms,
      })
      this.material.uniforms.uColorOverrideEnabled.value = 1
    }
    this.material.update(result.material)

    // update global shared avatar atlas
    // const material = this.client.avatars.material
    // material.update(result.material)

    if (this.geometry) {
      this.geometry.dispose()
    }

    this.geometry = new THREE.BufferGeometry()
    this.geometry.setAttribute('position', new THREE.BufferAttribute(result.geometry.position, 3))
    // this.geometry.setAttribute('normal', new THREE.BufferAttribute(result.geometry.normal, 3))
    this.geometry.setAttribute('tileId', new THREE.BufferAttribute(result.geometry.tileId, 1))
    this.geometry.setAttribute('uv', new THREE.BufferAttribute(result.geometry.uv, 2))
    this.geometry.setAttribute('light', new THREE.BufferAttribute(result.geometry.light, 4))
    this.geometry.setAttribute('ao', new THREE.BufferAttribute(result.geometry.ao, 1))
    this.geometry.setAttribute('dim', new THREE.BufferAttribute(result.geometry.dim, 1))
    this.geometry.setAttribute('wind', new THREE.BufferAttribute(result.geometry.wind, 1))
    this.geometry.setAttribute('skinIndex', new THREE.BufferAttribute(result.geometry.skinIndex, 4))
    this.geometry.setAttribute('skinWeight', new THREE.BufferAttribute(result.geometry.skinWeight, 4))
    this.geometry.setIndex(new THREE.BufferAttribute(result.geometry.index, 1))

    if (!this.mesh) {
      const bones = []
      const bonesByName = {}

      // create all bones
      result.skeleton.bones.forEach(boneData => {
        const bone = new THREE.Bone()
        bone.name = boneData.name
        bone.position.fromArray(boneData.position)
        bone.quaternion.fromArray(boneData.quaternion)
        bone.scale.fromArray(boneData.scale)
        bones.push(bone)
        bonesByName[boneData.name] = bone
      })

      // setup hierarchy
      result.skeleton.bones.forEach((boneData, i) => {
        if (boneData.parent && bonesByName[boneData.parent]) {
          bonesByName[boneData.parent].add(bones[i])
        }
      })

      // create bone inverses
      const boneInverses = result.skeleton.boneInverses.map(arr => new THREE.Matrix4().fromArray(arr))

      // create skeleton
      const skeleton = new THREE.Skeleton(bones, boneInverses)

      // create skinned mesh
      // const mesh = new THREE.SkinnedMesh(this.geometry, new THREE.MeshBasicMaterial({ color: 'red' }))
      const mesh = new THREE.SkinnedMesh(this.geometry, this.material.base)
      // mesh.customDepthMaterial = this.material.depth
      mesh.add(bones[0])
      mesh.bind(skeleton)

      // height is from root to eyes
      this.height = bonesByName.eyes.getWorldPosition(v1).y
      // const height = (1 / 1.5) * this.height // 1.5 is rig scale ;)
      // console.log('height', this.height)
      // const height = 0.4

      this.bonesByName = bonesByName

      // add to scene
      this.client.graphics.scene.add(mesh)

      this.mesh = mesh
    }

    if (this.mesh.geometry !== this.geometry) {
      this.mesh.geometry.dispose()
      this.mesh.geometry = this.geometry
    }

    // mesh.position.set(0, 10.6, -3)
    this.mesh.visible = this.visible

    // // cleanup / swap
    // if (this.mesh) {
    //   // copy over transforms
    //   mesh.position.copy(this.mesh.position)
    //   for (const name in bonesByName) {
    //     const curr = bonesByName[name]
    //     const prev = this.bonesByName[name]
    //     if (!prev) continue
    //     curr.position.copy(prev.position)
    //     curr.quaternion.copy(prev.quaternion)
    //     curr.scale.copy(prev.scale)
    //     curr.matrixWorld.copy(prev.matrixWorld)
    //     curr.matrix.copy(prev.matrix)
    //   }
    //   // destroy old
    //   this.client.graphics.scene.remove(this.mesh)
    //   this.mesh.geometry.dispose()
    // }

    // this.mesh = mesh
    // this.height = height

    // debug: hand and hints
    // {
    //   const mesh = new THREE.Mesh(
    //     new THREE.BoxGeometry(0.01, 0.01, 0.01),
    //     new THREE.MeshStandardMaterial({ color: 'white ' })
    //   )
    //   this.client.graphics.scene.add(mesh)
    //   this.lHintMesh = mesh
    // }
    // {
    //   const mesh = new THREE.Mesh(
    //     new THREE.BoxGeometry(0.01, 0.01, 0.01),
    //     new THREE.MeshStandardMaterial({ color: 'white ' })
    //   )
    //   this.client.graphics.scene.add(mesh)
    //   this.rHintMesh = mesh
    // }
    // {
    //   const mesh = new THREE.Mesh(
    //     new THREE.BoxGeometry(0.1, 0.1, 0.1),
    //     new THREE.MeshStandardMaterial({ color: 'white ' })
    //   )
    //   this.client.graphics.scene.add(mesh)
    //   this.lHandMesh = mesh
    // }
    // {
    //   const mesh = new THREE.Mesh(
    //     new THREE.BoxGeometry(0.1, 0.1, 0.1),
    //     new THREE.MeshStandardMaterial({ color: 'white ' })
    //   )
    //   this.client.graphics.scene.add(mesh)
    //   this.rHandMesh = mesh
    // }
  }

  update(delta, baseY, head, lHand, rHand) {
    if (!this.mesh) return
    // console.time('[avatar] update')

    const bones = this.bonesByName

    // turn mesh/root to face Y forward direction of our head
    // this.mesh.rotation.y = e1.copy(head.rotation).reorder('YXZ').y
    const yaw = e1.copy(head.rotation).reorder('YXZ').y
    this.mesh.rotation.y = yaw + Math.PI

    // rotate head bone to match our head
    const headParentWorldQuaternion = q1
    bones.head.parent.getWorldQuaternion(headParentWorldQuaternion)
    const headParentWorldQuaternionInverse = q2.copy(headParentWorldQuaternion).invert()
    const headLocalQuaternion = headParentWorldQuaternionInverse.multiply(head.quaternion)
    // apply 180° correction so the modeled face matches head forward
    headLocalQuaternion.multiply(HEAD_FORWARD_CORRECTION)
    bones.head.quaternion.copy(headLocalQuaternion)

    // move this.mesh completely so that it perfectly aligns with bones.eyes
    const eyesWorldPosition = v1
    bones.eyes.getWorldPosition(eyesWorldPosition)
    const offset = v2.subVectors(head.position, eyesWorldPosition)
    this.mesh.position.add(offset)

    // move back down to base height
    this.mesh.position.y = baseY

    // left arm IK
    this.solveArmIK(bones.leftUpperArm, bones.leftLowerArm, bones.leftHand, bones.leftHint, lHand, 'left')

    // right arm IK
    this.solveArmIK(bones.rightUpperArm, bones.rightLowerArm, bones.rightHand, bones.rightHint, rHand, 'right')

    // debug: hand and hints
    // bones.leftHint.getWorldPosition(this.lHintMesh.position)
    // bones.leftHint.getWorldQuaternion(this.lHintMesh.quaternion)
    // bones.rightHint.getWorldPosition(this.rHintMesh.position)
    // bones.rightHint.getWorldQuaternion(this.rHintMesh.quaternion)
    // bones.leftHand.getWorldPosition(this.lHandMesh.position)
    // bones.leftHand.getWorldQuaternion(this.lHandMesh.quaternion)
    // bones.rightHand.getWorldPosition(this.rHandMesh.position)
    // bones.rightHand.getWorldQuaternion(this.rHandMesh.quaternion)

    // console.timeEnd('[avatar] update')

    // debug
    // this.mesh.position.z -= 3
    // this.mesh.rotation.y = 180 * DEG2RAD

    if (this.material) {
      this.sampleLight(head, delta)
      // console.log(this.lightArr)
      this.material.uniforms.uColorOverride.value.fromArray(this.lightArr)
      // ====
      // loc1.world.copy(head.position)
      // const chunk = this.client.chunks.get(loc1.chunkId)
      // if (chunk) {
      //   const idx = loc1.voxelIdx
      //   this.material.uniforms.uColorOverride.value = chunk.getLight(idx)
      // }
      // =======
      // if (chunk) {
      //   const idx = loc1.voxelIdx
      //   this.light = chunk.getLight(idx)
      // }
      // // smooth transition
      // let curr = this.material.uniforms.uColorOverride.value
      // let target = this.light
      // if (curr !== target) {
      //   // interpolate
      //   const t = Math.min(delta / LIGHT_TIME, 1.0)
      //   curr += (target - curr) * t
      //   // if close enough, just snap to target
      //   if (Math.abs(target - curr) < 0.01) {
      //     curr = target
      //   }
      //   // update uniform
      //   this.material.uniforms.uColorOverride.value = curr
      // }
    }
  }

  sampleLight(head, delta) {
    // console.time('sampleLight')
    _arr.length = 0
    const getLightAt = (loc, notOpaqueSide) => {
      const chunk = this.client.chunks.get(loc.chunkId)
      if (chunk) {
        const idx = loc.voxelIdx
        if (notOpaqueSide) {
          const block = chunk.getBlockAtIdx(idx)
          if (block.opaque[notOpaqueSide.id]) {
            return null
          }
        }
        return chunk.getLight(idx)
      }
    }
    // get light at head position
    loc1.world.copy(head.position)
    const light = getLightAt(loc1)
    if (light) _arr.push(light)
    // get light in all directions
    for (const dir of Directions.all) {
      const nLoc = loc1[dir.name]
      const light = getLightAt(nLoc, dir.opposite)
      if (light) _arr.push(light)
    }
    // no light? no worries i guess
    if (!_arr.length) return
    // average neighbors
    const target = [0, 0, 0, 0]
    const len = _arr.length
    for (let i = 0; i < len; i++) {
      const l = _arr[i]
      target[0] += l[0]
      target[1] += l[1]
      target[2] += l[2]
      target[3] += l[3]
    }
    target[0] /= len
    target[1] /= len
    target[2] /= len
    target[3] /= len
    // simple interpolation over X seconds
    const T = Math.max(LIGHT_TIME || 0.001, 0.001)
    const t = Math.min(Math.max((delta || 0) / T, 0), 1) // clamp 0..1
    // lerp each channel
    for (let i = 0; i < 4; i++) {
      this.lightArr[i] += (target[i] - this.lightArr[i]) * t
      // optional clamp
      // if (this.lightArr[i] < 0) this.lightArr[i] = 0
      // if (this.lightArr[i] > MAX_LIGHT_LEVEL) this.lightArr[i] = MAX_LIGHT_LEVEL
    }
    // console.timeEnd('sampleLight')
  }

  solveArmIK(upperArm, lowerArm, hand, hint, target, side) {
    // upperArm.rotation.set(0, 0, 0)
    // lowerArm.rotation.set(0, 0, 0)
    // hand.rotation.set(0, 0, 0)

    // hand.parent.getWorldQuaternion(q1)
    // hand.quaternion.copy(q2.copy(q1).invert().multiply(target.quaternion))

    // ---------- temps (no allocations per frame if you hoist these as class fields) ----------
    const shoulder = v1
    const elbow = v2
    const wrist = v3
    const dir = v4
    const bendDir = v5
    const tmp = v6
    const parentWQ = q1
    const qInv = q2
    const fromAxis = v7
    const hintPos = v8
    const aimRot = q3
    const elbowNow = v9

    // ---------- lengths from the rest-pose hierarchy (fixed each frame) ----------
    // child offsets encode segment lengths in your setup
    const upperLen = lowerArm.position.length()
    const lowerLen = hand.position.length()

    // ---------- world-space positions ----------
    upperArm.getWorldPosition(shoulder)
    const tPos = target.position // already world-space in your update()

    // quick outs
    dir.subVectors(tPos, shoulder)
    const dist = dir.length()
    if (dist < 1e-6) return // target right on the shoulder; nothing sensible to do

    // ---------- reach clamping ----------
    // Clamp the target distance so the triangle is solvable and doesn't fully lock out
    const maxReach = upperLen + lowerLen - 1e-6
    const minReach = 0.47 // Math.max(Math.abs(upperLen - lowerLen) + 1e-6, 1e-6)
    // console.log(upperLen, lowerLen) // 0.314 0.516
    const clamped = THREE.MathUtils.clamp(dist, minReach, maxReach)

    // normalized shoulder->target direction
    dir.multiplyScalar(1 / dist)

    // ===========
    // OG hand hints
    // ===========

    // ---------- bend plane from hint ----------
    hint.getWorldPosition(hintPos)

    // Project (hint - shoulder) onto plane perpendicular to dir to get bend direction
    bendDir.subVectors(hintPos, shoulder)
    // remove component along dir
    bendDir.addScaledVector(dir, -bendDir.dot(dir))

    if (bendDir.lengthSq() < 1e-8) {
      // If hint is collinear, pick any stable perpendicular
      // Prefer world up unless parallel; otherwise use world X
      bendDir.copy(Math.abs(dir.dot(UP)) > 0.99 ? RIGHT : UP)
      bendDir.addScaledVector(dir, -bendDir.dot(dir))
    }
    bendDir.normalize()

    // ==========

    // ---------- triangle solution (place the elbow point) ----------
    // Solve for x along dir and y along bendDir from the shoulder
    const a = upperLen,
      b = lowerLen,
      c = clamped
    const x = (a * a - b * b + c * c) / (2 * c)
    const y2 = Math.max(a * a - x * x, 0)
    const y = Math.sqrt(y2)

    elbow.copy(shoulder).addScaledVector(dir, x).addScaledVector(bendDir, y)

    // The wrist target we aim to is the clamped point along dir
    wrist.copy(shoulder).addScaledVector(dir, c)

    // ---------- aim helper: rotate a bone so its local -Y axis points toward 'toDirWorld' ----------
    function aimBone(bone, toDirWorld) {
      // parent world quaternion
      bone.parent.getWorldQuaternion(parentWQ)

      // current "from" direction in world (local -Y transformed by parent)
      // bones extend along -Y in your rig
      const fromWorld = fromAxis.copy(DOWN).applyQuaternion(parentWQ)

      // rotation that takes fromWorld -> toDirWorld
      const rot = aimRot.setFromUnitVectors(fromWorld.normalize(), toDirWorld)

      // desired world rotation for the bone
      const desiredWorldQ = rot.multiply(parentWQ)

      // convert to local
      bone.quaternion.copy(qInv.copy(parentWQ).invert().multiply(desiredWorldQ))
    }

    // ---------- set upper arm rotation to point at elbow ----------
    const toElbow = tmp.subVectors(elbow, shoulder).normalize()
    aimBone(upperArm, toElbow)

    // desired bend/twist direction at the upper arm is the bendDir we computed
    twistBoneToHint(
      upperArm,
      toElbow,
      bendDir, // world-space vector toward hint plane
      v7,
      v8, // temps
      q1,
      q2,
      q3 // temps
    )

    // ---------- recompute elbow world pos after rotating upper (for stability) ----------
    // (positions are fixed, we just need the updated world elbow position)
    lowerArm.getWorldPosition(elbowNow)

    // ---------- set lower arm rotation to point from (new) elbow to clamped wrist ----------
    const toWrist = wrist.sub(elbowNow).normalize()
    aimBone(lowerArm, toWrist)

    // for the forearm, reuse the same bend plane (keeps elbow "pit" facing the hint)
    twistBoneToHint(lowerArm, toWrist, bendDir, v7, v8, q1, q2, q3)

    // ---------- optional: orient the hand to match target twist ----------
    // If you want the palm/hand to copy controller orientation:
    // hand.parent.getWorldQuaternion(parentWQ)
    // hand.quaternion.copy(qInv.copy(parentWQ).invert().multiply(target.quaternion))
    // Otherwise, leave the hand as-is and let downstream code apply posing.

    // ---------- orient the hand to match target with rest pose correction ----------
    hand.parent.getWorldQuaternion(parentWQ)
    const handRestOffset = side === 'left' ? HAND_REST_OFFSET_LEFT : HAND_REST_OFFSET_RIGHT
    // Apply: local = parent^-1 * target * restOffset
    hand.quaternion.copy(qInv.copy(parentWQ).invert().multiply(target.quaternion).multiply(handRestOffset))
  }

  copyBoneTransforms(oldBonesByName, newBonesByName) {
    for (const name in newBonesByName) {
      const oldBone = oldBonesByName[name]
      const newBone = newBonesByName[name]
      if (!oldBone || !newBone) continue

      // set world rotation
      oldBone.updateWorldMatrix(true, false)
      newBone.parent.updateWorldMatrix(true, false)

      const parentWorldQ = q1.copy(newBone.parent.getWorldQuaternion(q2))
      const parentWorldQInv = q2.copy(parentWorldQ).invert()
      const oldWorldQ = q3.copy(oldBone.getWorldQuaternion(q3))
      newBone.quaternion.copy(parentWorldQInv.multiply(oldWorldQ))

      // optionally match world position too (for leafs like eyes/hints)
      const oldWorldPos = v1.copy(oldBone.getWorldPosition(v1))
      const parentWorldMatInv = new THREE.Matrix4().copy(newBone.parent.matrixWorld).invert()
      newBone.position.copy(oldWorldPos.applyMatrix4(parentWorldMatInv))
    }
  }

  destroy() {
    if (this.mesh) {
      this.client.graphics.scene.remove(this.mesh)
      this.mesh = null
    }
  }
}

/**
 * helper: twist a bone so its local underside axis points to `desiredDirWorld` in the plane perpendicular to aimDirWorld
 */

const _underLocal = new THREE.Vector3(0, 0, 1) // "underside" axis in bone local space (adjust if needed)
const _cross = new THREE.Vector3()
const _localTwist = new THREE.Quaternion()
const _want = new THREE.Vector3()

function twistBoneToHint(bone, aimDirWorld, desiredDirWorld, tmpVecA, tmpVecB, parentWQ, qInv, qTwist) {
  // parent world quaternion
  bone.parent.getWorldQuaternion(parentWQ)

  // bone world quaternion = parent * local
  const boneWorldQ = qTwist.copy(parentWQ).multiply(bone.quaternion)

  // current underside axis in world, then project into plane ⟂ aimDir
  const underWorld = tmpVecA.copy(_underLocal).applyQuaternion(boneWorldQ)
  const aim = tmpVecB.copy(aimDirWorld).normalize()

  // project current underside and desired onto plane perpendicular to aim
  const proj = underWorld.addScaledVector(aim, -underWorld.dot(aim)).normalize()
  const want = _want.copy(desiredDirWorld).addScaledVector(aim, -desiredDirWorld.dot(aim)).normalize()

  // signed angle from proj -> want around aim
  const cross = _cross.crossVectors(proj, want)
  const sin = THREE.MathUtils.clamp(cross.dot(aim), -1, 1)
  const cos = THREE.MathUtils.clamp(proj.dot(want), -1, 1)
  const angle = Math.atan2(sin, cos)

  // rotate around local -Y by that angle (since local -Y == aim axis after aiming)
  const localTwist = _localTwist.setFromAxisAngle(DOWN, angle)
  bone.quaternion.multiply(localTwist) // local space multiply
}
